import { useState, useEffect, useContext, Fragment } from "react";
import { styled, useTheme } from "@mui/material/styles";
import { ToastContainer } from "react-toastify";
import {
  Box,
  CssBaseline,
  Toolbar,
  IconButton,
  Typography,
  Divider,
  List,
  Badge,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Button,
} from "@mui/material";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar from "@mui/material/AppBar";

import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import MenuIcon from "@mui/icons-material/Menu";
import PersonIcon from "@mui/icons-material/Person";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import InventoryIcon from "@mui/icons-material/Inventory";

// Menu Icons
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import GroupIcon from "@mui/icons-material/Group";
import SummarizeIcon from "@mui/icons-material/Summarize";
import HouseIcon from "@mui/icons-material/House";
import SettingsIcon from "@mui/icons-material/Settings";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import LogoutIcon from "@mui/icons-material/Logout";
import EventNoteIcon from "@mui/icons-material/EventNote";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ContactSupportIcon from "@mui/icons-material/ContactSupport";
import StorageIcon from "@mui/icons-material/Storage";
import PriceCheckIcon from "@mui/icons-material/PriceCheck";
import EmailIcon from "@mui/icons-material/Email";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ApiIcon from "@mui/icons-material/Api";
import BuildIcon from "@mui/icons-material/Build";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import SlideshowIcon from "@mui/icons-material/Slideshow";
import EngineeringIcon from "@mui/icons-material/Engineering";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import PhoneCallbackIcon from "@mui/icons-material/PhoneCallback";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";

import { Outlet, NavLink, useNavigate } from "react-router-dom";

import { Spinner, StockModal } from "../";

import permissions from "../../static/permissions";
import paymentServices from "../../services/paymentServices";

import { AuthContext } from "../../context/AuthProvider";

import "./layout.scss";

const navigation = [
  {
    text: "Appointments",
    icon: <CalendarMonthIcon />,
    permission: permissions.matrix,
  },
  {
    // permission "schedule" (not "matrix"): salesmen/technicians must see this item
    text: "Scheduled Appts",
    path: "scheduled-appointments",
    icon: <PhoneCallbackIcon />,
    permission: permissions.schedule,
  },
  {
    text: "Installations",
    icon: <BuildIcon />,
    permission: permissions.installations,
  },
  {
    text: "Schedule",
    icon: <EventNoteIcon />,
    permission: permissions.schedule,
  },
  {
    text: "Time-Tracking",
    icon: <AccessTimeIcon />,
    permission: permissions.timer,
  },
  {
    text: "Proposals",
    icon: <BusinessCenterIcon />,
    permission: permissions.proposals,
  },
  {
    text: "Clients",
    icon: <GroupIcon />,
    permission: permissions.clients,
  },
  {
    text: "Items",
    icon: <SummarizeIcon />,
    permission: permissions.items,
  },
  {
    text: "Equipment Catalog",
    path: "equipment-catalog",
    icon: <MenuBookIcon />,
    permission: permissions.proposals,
  },
  {
    text: "Properties",
    icon: <HouseIcon />,
    permission: permissions.properties,
  },
  {
    text: "Receivables",
    icon: <RequestQuoteIcon />,
    permission: permissions.payments,
  },
  {
    text: "Payments",
    path: "payments",
    icon: <PointOfSaleIcon />,
    permission: permissions.payments,
  },
  {
    text: "Settings",
    icon: <SettingsIcon />,
    permission: permissions.settings,
  },
  {
    text: "Logs",
    icon: <ReceiptLongIcon />,
    permission: permissions.logs,
  },
];

const drawerWidth = 240;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: 0,
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== "open" })(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

const Layout = () => {
  const [open, setOpen] = useState(false);
  const [stockModalOpen, setStockModalOpen] = useState(false);
  const [uncheckedPayments, setUncheckedPayments] = useState(0);

  // Badge on the Payments menu item: unchecked journal payments for today.
  useEffect(() => {
    if (!userData?.permissions?.includes(permissions.payments)) return undefined;
    let alive = true;
    const fetchCount = () =>
      paymentServices
        .getUncheckedCount()
        .then((res) => {
          if (alive) setUncheckedPayments(res.data?.count || 0);
        })
        .catch(() => {});
    fetchCount();
    const timer = setInterval(fetchCount, 120000);
    window.addEventListener('payments-unchecked-changed', fetchCount);
    return () => {
      alive = false;
      clearInterval(timer);
      window.removeEventListener('payments-unchecked-changed', fetchCount);
    };
  }, [userData]);

  const { userData, logout } = useContext(AuthContext);
  const theme = useTheme();
  const navigate = useNavigate();

  const drawerToggle = () => setOpen(!open);

  useEffect(() => {
    setOpen(false);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [window.location.pathname]);

  if (!userData) return <Spinner />;

  return (
    <Fragment>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />

        <AppBar position="fixed" open={open} sx={{ backgroundColor: "#F0F3F5", color: "#0D3B59" }}>
          <Toolbar sx={{ width: "100%" }}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={drawerToggle}
              edge="start"
              sx={{ marginRight: 5 }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" noWrap component="div" sx={{ fontFamily: "Days One, sans-serif" }}>
              Welcome To CRM
            </Typography>

            <Box sx={{ flexGrow: 1 }} />

            <Button
              variant="contained"
              size="small"
              startIcon={<InventoryIcon />}
              onClick={() => setStockModalOpen(true)}
              sx={{
                mr: 2,
                backgroundColor: "#0D3B59",
                "&:hover": { backgroundColor: "#1a5a8a" },
                textTransform: "none",
                fontWeight: "bold",
              }}
            >
              AIRMAXX STOCK
            </Button>

            <Tooltip title="Technical Support">
              <IconButton color="inherit" onClick={() => navigate("/support")}>
                <ContactSupportIcon />
              </IconButton>
            </Tooltip>
          </Toolbar>
        </AppBar>

        <Drawer variant="permanent" open={open}>
          <DrawerHeader>
            <Box
              sx={{
                pt: 1,
                pb: 1,
                img: {
                  maxHeight: 50,
                  width: "auto",
                  objectFit: "contain",
                },
              }}
            >
              <img src="/images/Airmaxx-Logo-Trans.png" alt="Logo" style={{ maxWidth: 180, height: "auto" }} />
            </Box>
            <IconButton onClick={drawerToggle}>
              {theme.direction === "rtl" ? <ChevronRightIcon /> : <ChevronLeftIcon />}
            </IconButton>
          </DrawerHeader>
          <Divider />
          <List>
            {navigation?.map((el, index) => {
              if (userData?.permissions.includes(el.permission)) {
                return (
                  <Fragment key={index}>
                    <ListItem disablePadding sx={{ display: "block" }}>
                      <NavLink to={el.path || el.text.toLocaleLowerCase()} className="navigation-link">
                        <ListItemButton
                          sx={{
                            minHeight: 48,
                            justifyContent: "initial",
                            px: 2.5,
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 0,
                              mr: 3,
                              justifyContent: "center",
                            }}
                          >
                            {el.text === "Payments" && uncheckedPayments > 0 ? (
                              <Badge badgeContent={uncheckedPayments} color="error" max={99}>
                                {el.icon}
                              </Badge>
                            ) : (
                              el.icon
                            )}
                          </ListItemIcon>
                          <ListItemText primary={el.text} />
                        </ListItemButton>
                      </NavLink>
                    </ListItem>
                    <Divider />
                  </Fragment>
                );
              }
              return false;
            })}

            {/* Low-price contract review queue: Emergency contact users only */}
            {(userData?.emergencyContact || userData?.role === "superadmin") && (
              <>
                <ListItem disablePadding sx={{ display: "block" }}>
                  <NavLink to="/low-price-requests" className="navigation-link">
                    <ListItemButton
                      sx={{
                        minHeight: 48,
                        justifyContent: "initial",
                        px: 2.5,
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 0, mr: 3, justifyContent: "center" }}>
                        <PriceCheckIcon />
                      </ListItemIcon>
                      <ListItemText primary="Low-Price Requests" />
                    </ListItemButton>
                  </NavLink>
                </ListItem>
                <Divider />
              </>
            )}

            {["superadmin", "admin", "dispatcher"].includes(userData?.role) && (
              <>
                <ListItem disablePadding sx={{ display: "block" }}>
                  <NavLink to="/archive" className="navigation-link">
                    <ListItemButton
                      sx={{
                        minHeight: 48,
                        justifyContent: "initial",
                        px: 2.5,
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 0, mr: 3, justifyContent: "center" }}>
                        <StorageIcon />
                      </ListItemIcon>
                      <ListItemText primary="Archive" />
                    </ListItemButton>
                  </NavLink>
                </ListItem>
                <Divider />
              </>
            )}

            {(userData?.role === "superadmin" || userData?.proVersion) && (
              <>
                <ListItem disablePadding sx={{ display: "block" }}>
                  <NavLink to="/presentation" className="navigation-link">
                    <ListItemButton
                      sx={{
                        minHeight: 48,
                        justifyContent: "initial",
                        px: 2.5,
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 0, mr: 3, justifyContent: "center" }}>
                        <SlideshowIcon />
                      </ListItemIcon>
                      <ListItemText primary="Presentation" />
                    </ListItemButton>
                  </NavLink>
                </ListItem>
                <Divider />
              </>
            )}

            {["superadmin", "admin", "dispatcher", "technician"].includes(userData?.role) && (
              <>
                <ListItem disablePadding sx={{ display: "block" }}>
                  <NavLink to="/warranty-tickets" className="navigation-link">
                    <ListItemButton
                      sx={{
                        minHeight: 48,
                        justifyContent: "initial",
                        px: 2.5,
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 0, mr: 3, justifyContent: "center" }}>
                        <EngineeringIcon />
                      </ListItemIcon>
                      <ListItemText primary="Warranty Tickets" />
                    </ListItemButton>
                  </NavLink>
                </ListItem>
                <Divider />
              </>
            )}

            {(["superadmin", "admin"].includes(userData?.role) ||
              userData?.permissions?.includes(permissions.waitList)) && (
              <>
                <ListItem disablePadding sx={{ display: "block" }}>
                  <NavLink to="/wait-list" className="navigation-link">
                    <ListItemButton
                      sx={{
                        minHeight: 48,
                        justifyContent: "initial",
                        px: 2.5,
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 0, mr: 3, justifyContent: "center" }}>
                        <PendingActionsIcon />
                      </ListItemIcon>
                      <ListItemText primary="Wait List" />
                    </ListItemButton>
                  </NavLink>
                </ListItem>
                <Divider />
              </>
            )}

            {userData?.role === "superadmin" && (
              <>
                <ListItem disablePadding sx={{ display: "block" }}>
                  <NavLink to="/mailer" className="navigation-link">
                    <ListItemButton
                      sx={{
                        minHeight: 48,
                        justifyContent: "initial",
                        px: 2.5,
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 0, mr: 3, justifyContent: "center" }}>
                        <EmailIcon />
                      </ListItemIcon>
                      <ListItemText primary="Email Logs" />
                    </ListItemButton>
                  </NavLink>
                </ListItem>
                <Divider />
              </>
            )}

            {userData?.role === "superadmin" && (
              <>
                <ListItem disablePadding sx={{ display: "block" }}>
                  <NavLink to="/data-import" className="navigation-link">
                    <ListItemButton
                      sx={{
                        minHeight: 48,
                        justifyContent: "initial",
                        px: 2.5,
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 0, mr: 3, justifyContent: "center" }}>
                        <CloudUploadIcon />
                      </ListItemIcon>
                      <ListItemText primary="Data Import" />
                    </ListItemButton>
                  </NavLink>
                </ListItem>
                <Divider />
              </>
            )}

            {userData?.role === "superadmin" && (
              <>
                <ListItem disablePadding sx={{ display: "block" }}>
                  <NavLink to="/api-integrations" className="navigation-link">
                    <ListItemButton
                      sx={{
                        minHeight: 48,
                        justifyContent: "initial",
                        px: 2.5,
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 0, mr: 3, justifyContent: "center" }}>
                        <ApiIcon />
                      </ListItemIcon>
                      <ListItemText primary="API Integrations" />
                    </ListItemButton>
                  </NavLink>
                </ListItem>
                <Divider />
              </>
            )}

            <ListItem disablePadding sx={{ display: "block" }}>
              <NavLink to="/profile" className="navigation-link">
                <ListItemButton
                  sx={{
                    minHeight: 48,
                    justifyContent: "initial",
                    px: 2.5,
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 0, mr: 3, justifyContent: "center" }}>
                    <PersonIcon />
                  </ListItemIcon>
                  <ListItemText primary="Profile" />
                </ListItemButton>
              </NavLink>
            </ListItem>

            <Divider />

            <ListItem disablePadding sx={{ display: "block" }}>
              <ListItemButton
                onClick={logout}
                sx={{
                  minHeight: 48,
                  justifyContent: "initial",
                  px: 2.5,
                }}
              >
                <ListItemIcon sx={{ minWidth: 0, mr: 3, justifyContent: "center" }}>
                  <LogoutIcon />
                </ListItemIcon>
                <ListItemText primary="Log Out" />
              </ListItemButton>
            </ListItem>
          </List>
        </Drawer>

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: { xs: 0.5, sm: 1.5, md: 3 },
            maxWidth: window.location.pathname === "/appointments" ? "unset !important" : "100%",
          }}
        >
          <DrawerHeader />
          <Outlet />
        </Box>
      </Box>
      <ToastContainer />
      <StockModal open={stockModalOpen} onClose={() => setStockModalOpen(false)} />
    </Fragment>
  );
};

export default Layout;
